import { createAction, createReducer } from "@reduxjs/toolkit";
import type { UserVM } from "@shared-kernel/application/ports/shared/shared-repository";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { retrieveUserProfile } from "@user-management-context/read/application/use-cases/user-profile/retrieve-user-profile";
import { setActiveRole } from "@user-management-context/write/application/use-cases/active-role/set-active-role";
import { logoutUser } from "@user-management-context/write/application/use-cases/user-logout/logout-user";
import { handleAuthError } from "@user-management-context/write/application/use-cases/handle-auth-error/handle-auth-error";
import { FORBIDDEN_ERROR_NAME, SESSION_UNAVAILABLE_ERROR_NAME } from "@shared-kernel/domain/errors/errors";

export type SessionStatus = "idle" | "syncing" | "active" | "terminated";

export type SessionTerminationReason = "no-token" | "auth-error" | "session-expired" | "unavailable";

export interface SessionState {
  status: SessionStatus;
  user: UserVM | null;
  activeRole: Nullable<ROLES>;
  impersonatedBy: { adminId: string } | null;
  // Set when a sync finds an impersonation that ended on its own (token expired)
  // rather than on the admin's request. Consumed once by the UI: a word to the
  // admin plus a trip back to their own home page.
  impersonationExpired: boolean;
  terminationReason: SessionTerminationReason | null;
  lastSyncedAt: number | null;
}

const initialState: SessionState = {
  status: "idle",
  user: null,
  activeRole: null,
  impersonatedBy: null,
  impersonationExpired: false,
  terminationReason: null,
  lastSyncedAt: null,
};

export const setImpersonatedBy = createAction<{ adminId: string } | null>("session/setImpersonatedBy");
export const markImpersonationExpired = createAction("session/markImpersonationExpired");
export const resetImpersonationExpired = createAction("session/resetImpersonationExpired");
export const markSessionSyncing = createAction("session/markSyncing");
export const markSessionActive = createAction<{ lastSyncedAt: number }>("session/markActive");
export const markSessionTerminated = createAction<SessionTerminationReason>("session/markTerminated");

export const sessionReducer = createReducer(initialState, builder => {
  builder.addCase(setImpersonatedBy, (state, action) => {
    state.impersonatedBy = action.payload;
  });
  builder.addCase(markImpersonationExpired, state => {
    state.impersonationExpired = true;
  });
  builder.addCase(resetImpersonationExpired, state => {
    state.impersonationExpired = false;
  });
  builder.addCase(setActiveRole, (state, action) => {
    state.activeRole = action.payload;
  });
  builder.addCase(retrieveUserProfile.fulfilled, (state, action) => {
    state.user = action.payload;
  });
  builder.addCase(markSessionSyncing, state => {
    state.status = "syncing";
  });
  builder.addCase(markSessionActive, (state, action) => {
    state.status = "active";
    state.terminationReason = null;
    state.lastSyncedAt = action.payload.lastSyncedAt;
  });
  builder.addCase(markSessionTerminated, (state, action) => {
    // A terminated session carries no identity. Two route guards read this state
    // and disagree the moment it holds both: ProtectedRoute sends "terminated" to
    // /login, LoginOutlet sends anything carrying a user away from /login, and the
    // two then navigate back and forth until the browser throttles them. Clearing
    // the identity here keeps that state unreachable whatever ended the session —
    // rather than relying on every caller to also dispatch handleAuthError.
    state.status = "terminated";
    state.terminationReason = action.payload;
    state.user = null;
    state.activeRole = null;
    state.impersonatedBy = null;
    // Nobody is left to read it: the admin is on their way to /login, where an
    // "impersonation expired" toast would only be a ghost of a dead session.
    state.impersonationExpired = false;
  });
  builder.addCase(logoutUser, () => ({
    ...initialState,
    status: "terminated",
  }));
  builder.addCase(handleAuthError, (state, action) => {
    // These two only report something to the user. A 403 refuses one action, and
    // an unreachable server says nothing about the session — neither is a reason to
    // sign anyone out, so the state (including why a sync stopped) is left alone.
    if (action.payload.name === FORBIDDEN_ERROR_NAME || action.payload.name === SESSION_UNAVAILABLE_ERROR_NAME) {
      return state;
    }
    // Keep why the session ended: syncSession recorded it just before, and this
    // action reacts to that — it should not erase the diagnosis it reacts to.
    return { ...initialState, status: "terminated" as const, terminationReason: state.terminationReason };
  });
});
